import { isPresentationMode } from 'env';
import {
  addComponentsAction,
  batchLoadComponents,
  bulkComponentsDeleted,
  componentSelectedInPresentationMode,
  createSurveyFromComponent,
  deleteComponent,
  imageDroppedOnComponentNode,
  lockComponents,
  mergeComponentChange,
  notifyComponentLocked,
  notifyComponentUnlocked,
  selectComponent,
  toggleLockComponent,
  setComponentLock as setComponentLockAction,
  unlockComponents,
  createComponent,
  createComponentFailed,
  createComponentSucceeded,
  createComponents,
  createComponentsFailed,
  createComponentsSucceeded,
  removeComponentsAction,
  resetComponentsAction,
  saveComponentsAction,
  updateComponentAction,
} from './ComponentActions';
import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
  spreadFilter,
} from '@ardoq/rxbeach';
import { scenarioComponentsSaved } from 'appModelStateEdit/actions';
import { tap, withLatestFrom, switchMap, map, filter } from 'rxjs/operators';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { createFromSelectedComponent } from 'surveyAdmin/surveyUtil';
import { readonlyFieldData$ } from 'surveyAdmin/streams/readonlyFieldData$';
import { selectNodeInPresentationMode } from 'presentation/slideNavigator/actions';
import { contextInterface } from 'modelInterface/contextInterface';
import { launchDeleteComponentsAndReferencesConfirmationDialogByIds } from 'components/Dialogs/confirmDeletion/confirmDeletion';
import { externallyManagedNotice$ } from 'externallyManaged/externallyManagedNotice$';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
  ProvenanceSource,
  ResourceType,
} from '@ardoq/api-types';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { components$, scenarioRelatedComponentIds$ } from './components$';
import { models$, ModelsState } from 'streams/models/models$';
import { componentOperations, modelOperations } from '@ardoq/core';
import { references$ } from 'streams/references/references$';
import { fields$, toFieldTypeKey } from 'streams/fields/fields$';
import { referenceOperations } from '@ardoq/reference-operations';
import { getReferenceTypesWithDefaults } from '@ardoq/data-model';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { tags$ } from 'streams/tags/tags$';
import {
  getConnectedWorkspaceIds,
  linkedWorkspaces$,
} from 'streams/linkedWorkspaces$';
import { alert } from '@ardoq/modal';
import { noWorkspacePermissionDialogConfig } from 'yfilesExtensions/view/NoWorkspacePermissionDialog';
import { lockedComponentDialog } from 'yfilesExtensions/view/LockedComponentDialog';
import { areFileSizesValid } from '../../documentArchive/attachmentUploadUtils';
import { unknownDocumentArchiveErrorDialog } from 'yfilesExtensions/view/NoDocumentArchivePermissionDialog';
import {
  attachmentApi,
  api,
  batchApi,
  ComponentCreate,
  handleError,
  scenarioApi,
} from '@ardoq/api';
import {
  type ArdoqError,
  isArdoqError,
  isTruthy,
  toArdoqError,
} from '@ardoq/common-helpers';
import { omit } from 'lodash';
import { v4 as uuid } from 'uuid';
import { context$, workspacesClosed$ } from 'streams/context/context$';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { dispatchWebsocketEvent, websocket$ } from 'sync/websocket$';
import {
  ArdoqEvent,
  EventType,
  isResourceCreate,
  isResourceDelete,
  isResourceUpdate,
  isWebSocketBulkDelete,
} from 'sync/types';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import {
  ardoqEventOperations,
  shouldProcessEventForBranch,
} from 'sync/ardoqEventOperations';
import { StreamCollection } from 'streams/utils/streamUtils';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { isNotHandledByViewpointModeRoutines } from 'loadedState/isHandledByViewpointModeRoutine';
import { clearComponent } from 'streams/context/ContextActions';
import { logError, logWarn } from '@ardoq/logging';
import { batchRemoveReferences } from 'streams/references/ReferenceActions';
import { upsertTags } from 'streams/tags/actions';
import { resetGraph } from 'modelInterface/graphModelActions';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';

const selectComponentHandler = routine(
  ofType(selectComponent),
  extractPayload(),
  withLatestFrom(context$, components$, scenarioRelatedComponentIds$),
  tap(([{ cid }, { isExploreMode }, componentsState, scenarioRelatedIds]) => {
    const component = componentsState.byId[cid];
    if (!component || scenarioRelatedIds.has(component._id)) {
      return;
    }

    if (isPresentationMode()) {
      dispatchAction(selectNodeInPresentationMode(component._id));
      if (!isExploreMode) return;
    }

    contextInterface.setComponentById(component._id);
  })
);

const selectComponentHandlerPresentationMode = routine(
  ofType(componentSelectedInPresentationMode),
  extractPayload(),
  withLatestFrom(components$),
  tap(([{ id }, componentsState]) => {
    const component = componentsState.byId[id];
    if (!component) return;
    dispatchAction(selectNodeInPresentationMode(component._id));
  })
);

const handleDeleteComponent = routine(
  ofType(deleteComponent),
  extractPayload(),
  withLatestFrom(externallyManagedNotice$),
  tap(([id, { isSuppressed: isExternallyManagedNoticeSuppressed }]) =>
    launchDeleteComponentsAndReferencesConfirmationDialogByIds({
      componentIds: [id],
      isExternallyManagedNoticeSuppressed,
    })
  )
);

const toLockUpdates = (
  componentIds: ArdoqId[],
  lock: ArdoqId | null,
  byId: Record<string, APIComponentAttributes>
): APIComponentAttributes[] =>
  componentIds
    .map(_id => byId[_id])
    .filter(isTruthy)
    .map(comp => ({ ...comp, lock }));

const handleLockComponents = routine(
  ofType(lockComponents),
  extractPayload(),
  withLatestFrom(components$, activeScenario$),
  map(([{ componentIds, userId }, currentComponents, { scenarioId }]) => ({
    updates: toLockUpdates(componentIds, userId, currentComponents.byId),
    scenarioId,
  })),
  filter(({ updates }) => updates.length > 0),
  switchMap(({ updates, scenarioId }) =>
    doUpdateComponents(updates, scenarioId)
  ),
  handleError(),
  tap(results =>
    results
      .filter(isTruthy)
      .forEach(comp => dispatchAction(notifyComponentLocked(comp._id)))
  )
);

const handleUnlockComponents = routine(
  ofType(unlockComponents),
  extractPayload(),
  withLatestFrom(components$, activeScenario$),
  map(([componentIds, currentComponents, { scenarioId }]) => ({
    updates: toLockUpdates(componentIds, null, currentComponents.byId),
    scenarioId,
  })),
  filter(({ updates }) => updates.length > 0),
  switchMap(({ updates, scenarioId }) =>
    doUpdateComponents(updates, scenarioId)
  ),
  handleError(),
  tap(results =>
    results
      .filter(isTruthy)
      .forEach(comp => dispatchAction(notifyComponentUnlocked(comp._id)))
  )
);

const handleToggleLockComponent = routine(
  ofType(toggleLockComponent),
  extractPayload(),
  withLatestFrom(components$, loadedCurrentUser$),
  tap(([id, currentComponents, { _id: userId }]) => {
    const component = currentComponents.byId[id];
    if (!component) return;
    if (component.lock) {
      dispatchAction(unlockComponents([id]));
    } else {
      dispatchAction(lockComponents({ componentIds: [id], userId }));
    }
  })
);

const handleSetComponentLock = routine(
  ofType(setComponentLockAction),
  extractPayload(),
  withLatestFrom(loadedCurrentUser$),
  tap(([{ componentId, locked }, { _id: userId }]) => {
    if (locked) {
      dispatchAction(lockComponents({ componentIds: [componentId], userId }));
    } else {
      dispatchAction(unlockComponents([componentId]));
    }
  })
);

const resolveComponentTypeName = (
  component: APIComponentAttributes,
  models: ModelsState
): string | null => {
  const model = models.byId[component.model];
  if (!model) return null;
  return (
    modelOperations.getModelTypes(model).componentTypesById[component.typeId]
      ?.name ?? null
  );
};

const resolveReferenceTypeName = (
  reference: APIReferenceAttributes,
  models: ModelsState
): string | null => {
  const model = models.byId[reference.model];
  if (!model) return null;
  if (reference.model === VIRTUAL_MODEL_ID) {
    return model.referenceTypes[reference.type]?.name ?? null;
  }
  return modelOperations.getRefTypeName(
    getReferenceTypesWithDefaults(model),
    reference.type
  );
};

const handleCreateSurveyFromComponent = routine(
  ofType(createSurveyFromComponent),
  extractPayload(),
  withLatestFrom(
    components$,
    context$,
    models$,
    fields$,
    references$,
    readonlyFieldData$
  ),
  tap(
    ([
      id,
      componentsState,
      context,
      modelsState,
      fieldsState,
      referencesState,
      readonlyFieldData,
    ]) => {
      if (!componentsState.byId[id]) return;

      const contextComponent = componentsState.byId[context.componentId];
      if (!contextComponent) return;

      const componentTypeName = resolveComponentTypeName(
        contextComponent,
        modelsState
      );
      if (!componentTypeName) return;

      const references = referencesState.models.filter(reference =>
        referenceOperations.isForComponent(reference, contextComponent._id)
      );
      const fields =
        fieldsState.byComponentType.get(
          toFieldTypeKey(contextComponent.model, contextComponent.typeId)
        ) ?? [];

      const componentIds = new Set<ArdoqId>([contextComponent._id]);
      for (const reference of references) {
        componentIds.add(reference.source);
        componentIds.add(reference.target);
      }
      const componentTypeNamesById = new Map<ArdoqId, string | null>(
        [...componentIds].map(componentId => {
          const component = componentsState.byId[componentId];
          return [
            componentId,
            component ? resolveComponentTypeName(component, modelsState) : null,
          ];
        })
      );
      const referenceTypeNamesById = new Map<ArdoqId, string | null>(
        references.map(reference => [
          reference._id,
          resolveReferenceTypeName(reference, modelsState),
        ])
      );

      createFromSelectedComponent({
        contextComponent,
        componentTypeName,
        fields,
        references,
        componentTypeNamesById,
        referenceTypeNamesById,
        readonlyFieldData,
      });
    }
  )
);

const handleImageDropOnComponentNode = routine(
  ofType(imageDroppedOnComponentNode),
  extractPayload(),
  withLatestFrom(
    components$,
    workspaces$,
    loadedCurrentUser$,
    currentUserPermissionContext$,
    activeScenario$
  ),
  switchMap(
    async ([
      { componentId, file },
      components,
      workspaces,
      currentUser,
      permissionContext,
      activeScenarioState,
    ]) => {
      const component = components.byId[componentId];
      if (!component) return;
      const workspaceId = component.rootWorkspace;
      const workspaceName = workspaces.byId[workspaceId]?.name ?? null;
      const documentArchiveId = currentUser.organization?._id ?? '';

      if (
        !workspaceAccessControlInterface.canEditWorkspace(
          permissionContext,
          workspaceId,
          activeScenarioState
        )
      ) {
        alert(noWorkspacePermissionDialogConfig(workspaceName));
        return;
      }

      if (lockOperations.isLocked(component.lock, getFeatureContext())) {
        alert(lockedComponentDialog());
        return;
      }

      if (!areFileSizesValid([file])) {
        alert({
          title: 'File is too big',
          subtitle: 'The file exceeds the maximum size of 20MB',
        });
        return;
      }

      const isFilenameTakenResult = await isFilenameTaken(
        file,
        'org',
        documentArchiveId
      );

      if (isArdoqError(isFilenameTakenResult)) {
        alert(unknownDocumentArchiveErrorDialog(workspaceName));
        return;
      }

      const newImageUri = isFilenameTakenResult
        ? `/api/attachment/org/${documentArchiveId}/${file.name}`
        : await uploadAndGetUri(file, documentArchiveId);
      if (isArdoqError(newImageUri)) {
        alert(unknownDocumentArchiveErrorDialog(workspaceName));
        return;
      }

      const updateResult = await doUpdateComponents(
        [{ ...component, image: newImageUri }],
        activeScenarioState.scenarioId
      );
      if (isArdoqError(updateResult)) {
        api.logErrorIfNeeded(updateResult);
      }
    }
  )
);

const uploadAndGetUri = async (
  file: File,
  documentArchiveId: ArdoqId
): Promise<string | ArdoqError> => {
  const result = await attachmentApi.uploadFiles({
    files: [file],
    typeId: documentArchiveId,
    type: 'org',
  });
  if (isArdoqError(result)) return result;
  return result[0].uri;
};

const isFilenameTaken = async (
  file: File,
  context: 'org' | 'workspace',
  id: ArdoqId
) => {
  const attachments =
    context === 'org'
      ? await attachmentApi.getOrgAttachments(id)
      : await attachmentApi.getWorkspaceAttachments(id);

  if (isArdoqError(attachments)) {
    return attachments;
  }
  return (
    attachments.filter(({ filename }) => filename === file.name).length > 0
  );
};

const createComponentsInScenario = async (
  components: Partial<APIComponentAttributes>[],
  scenarioId: ArdoqId
): Promise<APIComponentAttributes[]> => {
  // Scenarios cannot use batch api, and we cannot use concurrent code because
  // the backend has race-conditions on this endpoint. See ARD-9818
  const results: APIComponentAttributes[] = [];
  for (const attributes of components) {
    const result = await scenarioApi.createComponent({
      component: omit(attributes, '_id') as ComponentCreate,
      scenarioId,
    });
    if (isArdoqError(result)) {
      api.logErrorIfNeeded(result);
      continue;
    }
    results.push(result);
  }
  return results;
};

const batchCreateComponents = async (
  components: Partial<APIComponentAttributes>[],
  provenanceSource?: ProvenanceSource
): Promise<APIComponentAttributes[] | ArdoqError> => {
  const create = components.map(attrs => {
    const { _id: batchId, ...noIdAttrs } = attrs;
    return { ...noIdAttrs, batchId: batchId ?? `temp-${uuid()}` };
  });
  const response = await batchApi.execute(
    {
      options: { includeEntities: true },
      components: { create },
    },
    provenanceSource
  );
  if (isArdoqError(response)) return response;
  const resultMap = response.components.created ?? {};
  return create
    .map(({ batchId }) => resultMap[response.components.ids[batchId]])
    .filter(isTruthy);
};

const doCreateComponents = async (
  components: Partial<APIComponentAttributes>[],
  scenarioId: string | null,
  provenanceSource?: ProvenanceSource
): Promise<APIComponentAttributes[]> => {
  if (scenarioId) {
    return createComponentsInScenario(components, scenarioId);
  }
  const result = await batchCreateComponents(components, provenanceSource);
  if (isArdoqError(result)) {
    api.logErrorIfNeeded(result);
    return [];
  }
  return result;
};

const handleCreateComponent = routine(
  ofType(createComponent),
  extractPayload(),
  withLatestFrom(activeScenario$),
  switchMap(([{ provenanceSource, attributes: component }, { scenarioId }]) =>
    doCreateComponents([component], scenarioId, provenanceSource)
  ),
  tap(persistedEntities => {
    const component = persistedEntities[0];
    if (!component) {
      dispatchAction(createComponentFailed());
      return;
    }
    dispatchAction(createComponentSucceeded(component));
  }),
  handleError(error => {
    api.logErrorIfNeeded(error);
    dispatchAction(createComponentFailed());
  })
);

const handleCreateComponents = routine(
  ofType(createComponents),
  extractPayload(),
  withLatestFrom(activeScenario$),
  tap(async ([{ components, provenanceSource }, { scenarioId }]) => {
    try {
      const persistedEntities = await doCreateComponents(
        components,
        scenarioId,
        provenanceSource
      );
      dispatchAction(createComponentsSucceeded(persistedEntities));
    } catch (error) {
      dispatchAction(
        createComponentsFailed(
          toArdoqError({
            error: error instanceof Error ? error : new Error(String(error)),
          })
        )
      );
    }
  })
);

type ComponentWithContext = {
  component: APIComponentAttributes;
  workspaceIds: string[];
  user: { name: string; id: ArdoqId };
  org: { label: string; _id: ArdoqId };
};

const toComponentWithContext = ([component, context, currentUser]: [
  APIComponentAttributes,
  { workspaceIds: string[] },
  { name: string; _id: string; organization: { label: string; _id: string } },
]): ComponentWithContext => ({
  component,
  workspaceIds: context.workspaceIds,
  user: { name: currentUser.name, id: currentUser._id },
  org: {
    label: currentUser.organization.label,
    _id: currentUser.organization._id,
  },
});

const workspaceOfComponentIsNotSubscribedToWebsocketEvents = ({
  component,
  workspaceIds,
}: ComponentWithContext) => !workspaceIds.includes(component.rootWorkspace);

const dispatchSyntheticWebsocketEvent = ({
  component,
  user,
  org,
}: ComponentWithContext) => {
  const syntheticEvent: ArdoqEvent<APIComponentAttributes> = {
    token: '',
    'resource-type': ResourceType.COMPONENT,
    'event-type': EventType.CREATE,
    data: component,
    user,
    org,
  };
  dispatchWebsocketEvent(syntheticEvent);
};

const handleBatchLoadComponents = routine(
  ofType(batchLoadComponents),
  extractPayload(),
  withLatestFrom(context$, linkedWorkspaces$),
  tap(([components, context, linkedWorkspaces]) => {
    const connectedWorkspaces = getConnectedWorkspaceIds(
      context.workspaceIds,
      linkedWorkspaces
    );
    const addedComponents = components.filter(attributes =>
      connectedWorkspaces.has(attributes.rootWorkspace)
    );
    if (addedComponents.length === 0) return;
    dispatchAction(addComponentsAction({ components: addedComponents }));
  })
);

const sameMembership = (
  a: Array<number | string>,
  b: Array<number | string>
): boolean => {
  const aSet = new Set(a);
  const bSet = new Set(b);
  if (aSet.size !== bSet.size) return false;
  for (const v of aSet) {
    if (!bSet.has(v)) return false;
  }
  return true;
};

const handleBulkComponentsDeleted = routine(
  ofType(bulkComponentsDeleted),
  extractPayload(),
  withLatestFrom(references$, tags$),
  tap(
    ([
      { componentIds, referenceIds, updatedTags },
      referencesState,
      tagsState,
    ]) => {
      const referenceIdsToRemove = referenceIds.filter(
        id => referencesState.byId[id] !== undefined
      );
      if (referenceIdsToRemove.length > 0) {
        dispatchAction(batchRemoveReferences(referenceIdsToRemove));
      }

      if (componentIds.length > 0) {
        dispatchAction(removeComponentsAction({ ids: componentIds }));
      }

      const tagsToUpdate = updatedTags.filter(tag => {
        const current = tagsState.byId[tag._id];
        if (!current) return false;
        return (
          !sameMembership(current.components, tag.components) ||
          !sameMembership(current.references, tag.references)
        );
      });
      if (tagsToUpdate.length > 0) {
        dispatchAction(upsertTags(tagsToUpdate));
      }
    }
  )
);

const handleComponentEvent = async (
  event: ArdoqEvent<unknown>,
  componentsState: StreamCollection<APIComponentAttributes>,
  selectedComponentId: ArdoqId | null
): Promise<void> => {
  if (isResourceUpdate(event)) {
    if (!ardoqEventOperations.eventHasVersionedResource(event)) {
      logWarn(new Error('Websocket event with unexpected data'), null, {
        eventType: event['event-type'],
        resourceType: event['resource-type'],
      });
      return;
    }
    const attributes = event.data as APIComponentAttributes;
    const oldParent = componentsState.byId[attributes._id]?.parent ?? null;
    const wasSelected = selectedComponentId === attributes._id;
    dispatchAction(addComponentsAction({ components: [attributes] }));
    if (wasSelected && oldParent !== (attributes.parent ?? null)) {
      dispatchAction(clearComponent());
      await contextInterface.setComponentById(attributes._id);
    }
    return;
  }

  if (isResourceCreate(event)) {
    if (!ardoqEventOperations.eventHasVersionedResource(event)) {
      logWarn(new Error('Websocket event with unexpected data'), null, {
        eventType: event['event-type'],
        resourceType: event['resource-type'],
      });
      return;
    }
    dispatchAction(
      addComponentsAction({
        components: [event.data as APIComponentAttributes],
      })
    );
    return;
  }

  if (isResourceDelete(event)) {
    return;
  }

  if (isWebSocketBulkDelete(event)) {
    dispatchAction(bulkComponentsDeleted(event.data));
    return;
  }
};

const handleComponentsWebsocketRoutine = websocket$.pipe(
  withLatestFrom(isViewpointMode$),
  spreadFilter(isNotHandledByViewpointModeRoutines),
  filter(ardoqEventOperations.isComponentEvent),
  withLatestFrom(activeBranchId$),
  spreadFilter(shouldProcessEventForBranch),
  withLatestFrom(components$, context$),
  tap(([event, componentsState, context]) => {
    handleComponentEvent(
      event,
      componentsState,
      context.componentId || null
    ).catch(error => {
      logError(
        error instanceof Error ? error : new Error('Unknown error'),
        'Unexpected error in component websocket handler',
        {
          eventType: event['event-type'],
          resourceType: event['resource-type'],
        }
      );
    });
  })
);

const handleCreateComponentSucceededInUnsubscribedWorkspace = routine(
  ofType(createComponentSucceeded),
  extractPayload(),
  withLatestFrom(context$, loadedCurrentUser$),
  map(toComponentWithContext),
  filter(workspaceOfComponentIsNotSubscribedToWebsocketEvents),
  tap(dispatchSyntheticWebsocketEvent)
);

const updateComponentsInScenario = async (
  attributes: APIComponentAttributes[],
  scenarioId: string
): Promise<APIComponentAttributes[]> => {
  const results: APIComponentAttributes[] = [];
  for (const component of attributes) {
    const result = await scenarioApi.updateComponent({
      component,
      scenarioId,
    });
    if (isArdoqError(result)) {
      api.logErrorIfNeeded(result);
      continue;
    }
    results.push(result);
  }
  return results;
};

const batchUpdateComponents = async (
  attributes: APIComponentAttributes[],
  provenanceSource?: ProvenanceSource
): Promise<APIComponentAttributes[] | ArdoqError> => {
  const response = await batchApi.execute(
    {
      options: { includeEntities: true },
      components: { update: attributes },
    },
    provenanceSource
  );

  if (isArdoqError(response)) {
    api.logErrorIfNeeded(response);
    return response;
  }

  const updatedMap = response.components.updated ?? {};
  return attributes.map(({ _id }) => updatedMap[_id]);
};

const doUpdateComponents = async (
  attributes: APIComponentAttributes[],
  scenarioId: string | null,
  provenanceSource?: ProvenanceSource
): Promise<APIComponentAttributes[] | ArdoqError> => {
  if (scenarioId) return updateComponentsInScenario(attributes, scenarioId);
  return batchUpdateComponents(attributes, provenanceSource);
};

const handleSaveComponents = routine(
  ofType(saveComponentsAction),
  extractPayload(),
  withLatestFrom(activeScenario$),
  tap(async ([{ attributes, provenanceSource }, { scenarioId }]) => {
    try {
      const saved = await doUpdateComponents(
        attributes,
        scenarioId,
        provenanceSource
      );
      if (isArdoqError(saved)) return;
      saved
        .filter(isTruthy)
        .forEach(component => dispatchAction(updateComponentAction(component)));
    } catch (error) {
      logError(
        error instanceof Error ? error : new Error(String(error)),
        'Failed to save components'
      );
    }
  })
);

const addWithParents = (
  keep: Set<ArdoqId>,
  comp: APIComponentAttributes,
  byId: Record<ArdoqId, APIComponentAttributes>
): void => {
  if (keep.has(comp._id)) return;
  keep.add(comp._id);
  const parent = comp.parent ? byId[comp.parent] : undefined;
  if (parent) addWithParents(keep, parent, byId);
};

const getComponentsToRemoveOnWorkspaceClose = (
  byId: Record<ArdoqId, APIComponentAttributes>,
  references: APIReferenceAttributes[],
  openWorkspaceIdSet: Set<ArdoqId>
): APIComponentAttributes[] => {
  const keep = new Set<ArdoqId>();
  references.forEach(ref => {
    if (
      !openWorkspaceIdSet.has(ref.rootWorkspace) &&
      openWorkspaceIdSet.has(ref.targetWorkspace)
    ) {
      const source = byId[ref.source];
      if (source) addWithParents(keep, source, byId);
    }
    if (
      openWorkspaceIdSet.has(ref.rootWorkspace) &&
      !openWorkspaceIdSet.has(ref.targetWorkspace)
    ) {
      const target = byId[ref.target];
      if (target) addWithParents(keep, target, byId);
    }
  });
  return Object.values(byId).filter(
    c => !keep.has(c._id) && !openWorkspaceIdSet.has(c.rootWorkspace)
  );
};

const handleMergeComponentChange = routine(
  ofType(mergeComponentChange),
  extractPayload(),
  withLatestFrom(components$, models$),
  tap(([patch, componentsState, modelsState]) => {
    const current = componentsState.byId[patch._id];
    if (!current) return;
    const updated: APIComponentAttributes = { ...current, ...patch };
    const modelData = modelsState.byId[updated.model];
    const patches = modelData
      ? componentOperations.buildParentChangeCascade(
          updated,
          componentsState.byId,
          componentsState.childrenByParent,
          modelData
        )
      : [updated];
    patches.forEach(p => dispatchAction(updateComponentAction(p)));
  })
);

const handleWorkspacesClosed = workspacesClosed$.pipe(
  withLatestFrom(components$, references$),
  tap(([{ workspaceIds }, componentsState, referencesState]) => {
    if (workspaceIds.length === 0) {
      dispatchAction(resetComponentsAction());
      dispatchAction(resetGraph());
      return;
    }

    const openWorkspaceIdSet = new Set(workspaceIds);
    const toRemove = getComponentsToRemoveOnWorkspaceClose(
      componentsState.byId,
      referencesState.models,
      openWorkspaceIdSet
    );
    if (toRemove.length === 0) return;
    dispatchAction(removeComponentsAction({ ids: toRemove.map(c => c._id) }));
  })
);

const handleScenarioComponentsSaved = routine(
  ofType(scenarioComponentsSaved),
  extractPayload(),
  tap(components =>
    components.forEach(component =>
      dispatchAction(updateComponentAction(component))
    )
  )
);

export const routines = collectRoutines(
  selectComponentHandler,
  handleDeleteComponent,
  handleLockComponents,
  handleUnlockComponents,
  handleToggleLockComponent,
  handleSetComponentLock,
  handleCreateSurveyFromComponent,
  selectComponentHandlerPresentationMode,
  handleImageDropOnComponentNode,
  handleBatchLoadComponents,
  handleCreateComponent,
  handleCreateComponents,
  handleCreateComponentSucceededInUnsubscribedWorkspace,
  handleSaveComponents,
  handleBulkComponentsDeleted,
  handleMergeComponentChange,
  handleWorkspacesClosed,
  handleComponentsWebsocketRoutine,
  handleScenarioComponentsSaved
);
